import { useState } from "react";
import { PlayCircle, Video } from "lucide-react";
import type { VideoConfig } from "@/lib/video-embeds";

type Props = {
  video: VideoConfig;
  /** Optional eyebrow label (defaults to "Watch the 30-second walkthrough"). */
  eyebrow?: string;
};

/**
 * Above-the-fold YouTube video slot. Lazy-mounts the iframe on first click so
 * we never block the page's LCP, but the surrounding <a> + thumbnail give
 * Googlebot a crawlable video doorway even before hydration.
 */
export function VideoEmbed({ video, eyebrow }: Props) {
  const [active, setActive] = useState(false);
  const [thumb, setThumb] = useState<string>(
    video.thumbnailUrl ?? `https://i.ytimg.com/vi/${video.videoId}/maxresdefault.jpg`,
  );
  const watchUrl = `https://www.youtube.com/watch?v=${video.videoId}`;
  const embedUrl = `https://www.youtube.com/embed/${video.videoId}?autoplay=1&rel=0`;

  return (
    <section
      itemScope
      itemType="https://schema.org/VideoObject"
      className="my-4 overflow-hidden rounded-2xl border border-border bg-card/60 shadow-sm"
    >
      <meta itemProp="name" content={video.name} />
      <meta itemProp="description" content={video.description} />
      <meta itemProp="uploadDate" content={video.uploadDate} />
      <meta itemProp="thumbnailUrl" content={thumb} />
      <meta itemProp="embedUrl" content={`https://www.youtube.com/embed/${video.videoId}`} />
      <meta itemProp="contentUrl" content={watchUrl} />

      <header className="flex items-center gap-2 px-4 pt-3 pb-2">
        <Video className="h-4 w-4 text-amber-600" />
        <p className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-300">
          {eyebrow ?? "Watch the 30-second walkthrough"}
        </p>
      </header>

      <div className="relative aspect-video w-full bg-black">
        {active ? (
          <iframe
            className="absolute inset-0 h-full w-full"
            src={embedUrl}
            title={video.name}
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          />
        ) : (
          <a
            href={watchUrl}
            onClick={(e) => {
              e.preventDefault();
              setActive(true);
            }}
            className="group absolute inset-0 block"
            aria-label={`Play video: ${video.name}`}
          >
            <img
              src={thumb}
              alt={video.name}
              loading="lazy"
              width={1280}
              height={720}
              className="h-full w-full object-cover transition group-hover:scale-[1.02]"
              onLoad={(e) => {
                // YouTube returns a 120x90 gray placeholder (200 OK) when
                // maxresdefault doesn't exist — onError never fires. Detect
                // by natural size and fall back to hqdefault.
                const img = e.currentTarget as HTMLImageElement;
                if (img.naturalWidth > 0 && img.naturalWidth <= 120) {
                  setThumb(`https://i.ytimg.com/vi/${video.videoId}/hqdefault.jpg`);
                }
              }}
              onError={() => {
                setThumb(`https://i.ytimg.com/vi/${video.videoId}/hqdefault.jpg`);
              }}
            />
            <span className="absolute inset-0 flex items-center justify-center">
              <PlayCircle className="h-16 w-16 text-white drop-shadow-lg transition group-hover:scale-110" />
            </span>
          </a>
        )}
      </div>

      <figcaption className="px-4 py-3 text-sm">
        <p className="font-semibold leading-snug">{video.name}</p>
        <p className="mt-1 text-xs text-muted-foreground">{video.description}</p>
      </figcaption>
    </section>
  );
}
